import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ColumnsConfig, TableConfig, TableData } from 'sct-custom-table/sct-table/projects/sct-table/src/lib/custom-table-interface';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { SitesService } from 'src/app/sites/sites.service';
import { ExcelService, ExcelSheet } from 'src/app/shared/excel/excel.service';
import { CommonService } from 'src/app/shared/services/common.service';

@Component({
	selector: 'app-iotemeter-site-exception-reports',
	templateUrl: './iotemeter-site-exception-reports.component.html',
	styleUrls: ['./iotemeter-site-exception-reports.component.css']
})
export class IotemeterSiteExceptionReportsComponent implements OnInit, OnDestroy {

	constructor(
		private translateService: TranslateService,
		private sideMenuService: SideMenuService,
		private sitesService: SitesService,
		private excelService: ExcelService,
		private commonService: CommonService,
	) { }

	readonly periods = [
		{ value: 'last_week', label: this.translateService.instant('durations.last_week') },
		{ value: 'last_7_days', label: this.translateService.instant('durations.last_7_days') },
		{ value: 'last_30_days', label: this.translateService.instant('durations.last_30_days') },
		{ value: 'last_90_days', label: this.translateService.instant('durations.last_90_days') },
		{ value: 'year_to_date', label: this.translateService.instant('durations.year_to_date') },
		{ value: 'last_year', label: this.translateService.instant('durations.last_year') },
		{ value: 'since_installation', label: this.translateService.instant('durations.all_years') },
	];

	siteSubscription: Subscription = new Subscription();
	getImExceptionReportsSubscription: Subscription = new Subscription();

	siteInfo: any;
	meterDisconnectionDevices: any;
	lowKwhDevices: any;
	highKwDevices: any;
	selectedPeriod = this.periods[0].value;

	meterDisconnectionReportTableConfig = {
		column: [] as ColumnsConfig[],
		data: [] as TableData[],
		config: this.createTableConfig('iotemeter.site.meter_disconnection_report')
	};

	lowMeterkWhUtilizationReportTableConfig = {
		column: [] as ColumnsConfig[],
		data: [] as TableData[],
		config: this.createTableConfig('iotemeter.site.low_meter_kWh_utilization_report')
	};

	highDailyKwDemandReportTableConfig = {
		column: [] as ColumnsConfig[],
		data: [] as TableData[],
		config: this.createTableConfig('iotemeter.site.high_daily_kw_demand_report')
	};

	createTableConfig(fileNameKey: string, csvHeaderInfo: any[] = []): TableConfig {
		return {
			hasExport: true,
			hasPagination: true,
			draggable: true,
			pageSize: 100,
			fileName: this.translateService.instant(fileNameKey),
			csvFileHeaderInfo: csvHeaderInfo
		};
	}

	getCsvHeaderInfo(includeSpacing = false): any[] {
		if (!this.siteInfo) return [];

		return this.commonService.getCsvFileHeadersInfo({
			customerName: this.siteInfo.customer_name,
			siteName: this.siteInfo.name,
			includeSpacing: includeSpacing
		});
	}

	getBaseColumns(): ColumnsConfig[] {
		return [
			{
				key: "serial_number",
				name: this.translateService.instant("iotemeter.devices.iotemeter_sn"),
				type: 'link'
			},
			{
				key: "iotemeter_id",
				name: this.translateService.instant("iotemeter.devices.iotemeter_id"),
				type: 'string'
			}
		];
	}

	buildColumns(extra: { key: string; label: string; tooltip?: string }): ColumnsConfig[] {
		return [
			...this.getBaseColumns(),
			{
				key: extra.key,
				name: this.translateService.instant(extra.label),
				type: 'string',
				hasTooltip: extra.tooltip ? this.translateService.instant(extra.tooltip) : null,
			}
		];
	}

	reportColumnMap = {
		meterDisconnection: {
			key: 'max_disconnect_days',
			label: 'iotemeter.devices.max_disconnect_days'
		},
		lowKwh: {
			key: 'min_daily_kwh_consumption',
			label: 'iotemeter.devices.min_daily_kwh_consumption',
			tooltip: 'exception_reports.low_kwh_utilization_tooltip'
		},
		highKw: {
			key: 'max_daily_kw_demand',
			label: 'iotemeter.devices.max_daily_kw_demand',
			tooltip: 'exception_reports.high_kw_demand_tooltip'
		}
	};


	fillReportsCols(): void {
		this.meterDisconnectionReportTableConfig.column =
			this.buildColumns(this.reportColumnMap.meterDisconnection);

		this.lowMeterkWhUtilizationReportTableConfig.column =
			this.buildColumns(this.reportColumnMap.lowKwh);

		this.highDailyKwDemandReportTableConfig.column =
			this.buildColumns(this.reportColumnMap.highKw);
	}
	
	ngOnInit(): void {
		this.siteSubscription = this.sideMenuService.currentSite.subscribe((site: any) => {
			if (site && site.installation_date)
				this.siteInfo = site;

			if (this.siteInfo?.id)
				this.getData();

			const csvHeaderInfo = this.getCsvHeaderInfo();
			this.meterDisconnectionReportTableConfig.config.csvFileHeaderInfo = csvHeaderInfo;
			this.lowMeterkWhUtilizationReportTableConfig.config.csvFileHeaderInfo = csvHeaderInfo;
			this.highDailyKwDemandReportTableConfig.config.csvFileHeaderInfo = csvHeaderInfo;
			this.fillReportsCols();
		});
	}

	getData() {
		const criteria = {
			siteId: this.siteInfo.id,
			period: this.selectedPeriod
		};

		this.getImExceptionReportsSubscription = this.sitesService.getImExceptionReports(criteria).subscribe((data: any) => {
			this.meterDisconnectionDevices = data.meterDisconnectionReport || [];
			this.lowKwhDevices = data.lowMeterkWhUtilizationReport || [];
			this.highKwDevices = data.highDailykWDemandReport || [];
			this.fillDataTable();
		});
	}

	reportDataColumnMap = {
		meterDisconnection: {
			key: 'max_disconnect_days',
			label: 'iotemeter.devices.max_disconnect_days',
			getValue: (device: any) => device.disconnect_days,
		},
		lowKwh: {
			key: 'min_daily_kwh_consumption',
			label: 'iotemeter.devices.min_daily_kwh_consumption',
			getValue: (device: any) => device.min_count_days_consumed_energy
		},
		highKw: {
			key: 'max_daily_kw_demand',
			label: 'iotemeter.devices.max_daily_kw_demand',
			getValue: (device: any) => device.max_count_days_consumed_energy
		}
	};

	buildTableData(devices: any[], reportKey: keyof typeof this.reportDataColumnMap): TableData[] {
		const report = this.reportDataColumnMap[reportKey];
 
		return devices.map((device: any) => ({
			serial_number: {
				value: device.serial_number,
				link: ['/', this.siteInfo.customer_id, this.siteInfo.id, device.mac_address, "performance"]
			},
			iotemeter_id: {
				value: device.iotemeter_id || '-' //todo
			},
			[report.key]: {
				value: report.getValue(device)
			}
		}));
	}

	fillDataTable() {
		this.meterDisconnectionReportTableConfig.data = this.buildTableData(this.meterDisconnectionDevices, 'meterDisconnection');

		this.lowMeterkWhUtilizationReportTableConfig.data = this.buildTableData(this.lowKwhDevices, 'lowKwh');

		this.highDailyKwDemandReportTableConfig.data = this.buildTableData(this.highKwDevices, 'highKw');
	}

	exportReports() {
		const data = this.prepareDataForExcelExport();
		this.excelService.exportExcel(data, `Exception Report.xlsx`);
	}

	prepareDataForExcelExport() {
		const sheets: ExcelSheet[] = [];

		const baseHeaders = this.commonService.getCsvFileHeadersInfo({
			customerName: this.siteInfo.customer_name,
			siteName: this.siteInfo.name,
			includeSpacing: false
		});
		
		const reports = [
			{
				title: this.translateService.instant('iotemeter.site.meter_disconnection_report'),
				devices: this.meterDisconnectionDevices,
				column: this.reportDataColumnMap.meterDisconnection,
				csvHeaderInfo: this.meterDisconnectionReportTableConfig.config.csvFileHeaderInfo
			},
			{
				title: this.translateService.instant('iotemeter.site.low_meter_kWh_utilization_report'),
				devices: this.lowKwhDevices,
				column: this.reportDataColumnMap.lowKwh,
				csvHeaderInfo: this.lowMeterkWhUtilizationReportTableConfig.config.csvFileHeaderInfo
			},
			{
				title: this.translateService.instant('iotemeter.site.high_daily_kw_demand_report'),
				devices: this.highKwDevices,
				column: this.reportDataColumnMap.highKw,
				csvHeaderInfo: this.highDailyKwDemandReportTableConfig.config.csvFileHeaderInfo
			}
		];

		for (const report of reports) {
			const headerRow = [
				this.translateService.instant("iotemeter.devices.iotemeter_sn"),
				this.translateService.instant("iotemeter.devices.iotemeter_id"),
				this.translateService.instant(report.column.label)
			];

			const header = [
				...baseHeaders,
				['', ''],
				['', ''],
				headerRow
			];

			const sheetData = (report.devices || []).map((device: any) => ({
				serial_number: device.serial_number,
				iotemeter_id: device.iotemeter_id || '-',
				[report.column.key]: report.column.getValue(device)
			}));

			sheets.push({
				header: header,
				data: sheetData,
				sheetName: report.title
			});
		}
		return sheets;
	}

	ngOnDestroy(): void {
		this.siteSubscription.unsubscribe();
	}
}
